import { IntegrityEdit } from './../model/integrity-edit';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { SettingService } from 'src/app/core/service/setting.service';
import { OrganizationEdit } from 'src/app/organization/model/organizationEdit';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UserService } from 'src/app/core/service/user.service';
import { IntegrityService } from 'src/app/core/service/integrity.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';

@Component({
  standalone: false,
  selector: 'app-integrity-edit',
  templateUrl: './integrity-edit.component.html',
  styleUrls: ['./integrity-edit.component.scss']
})
export class IntegrityEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public isNew = false;
  @Input() public integrityTypes = [];

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<IntegrityEdit> = new EventEmitter();

  public patientLabel = {};
  public editForm: any;
  public editFormActive = false;
  public range = { start: null, end: null };  // Temporary: Will be moved to the form
  public isLocked = false;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * All organizations
   */
  public allOrganizations = [];

  /**
   * Filtered list of organizations for drop-down.
   */
  public organizationsFiltered: OrganizationEdit[] = [];
  public virtualOrganization: any = { itemHeight: 30 };

  /**
   * All users
   */
  public allUsers = [];

  /**
   * Filtered list of users for drop-down.
   */
  public usersFiltered: OrganizationEdit[] = [];
  public virtualUser: any = { itemHeight: 30 };

  constructor(
    private settingService: SettingService,
    private integrityService: IntegrityService,
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private organizationService: OrganizationService,
    private userService: UserService)
  {
    this.loadLookups();
  }

  public get integrityType(): FormControl
  {
    return this.editForm.get('integrityType');
  }

  public get comments(): FormControl
  {
    return this.editForm.get('comments');
  }

  public get userSelection(): FormControl
  {
    return this.editForm.get('userSelection');
  }

  public get automaticValidations(): FormControl
  {
    return this.editForm.get('automaticValidations');
  }

  public get automaticDiscardOnError(): FormControl
  {
    return this.editForm.get('automaticDiscardOnError');
  }

  public get fromDateSelection(): FormControl
  {
    return this.editForm.get('fromDateSelection');
  }

  public get toDateSelection(): FormControl
  {
    return this.editForm.get('toDateSelection');
  }

  public get lastNameSelection(): FormControl
  {
    return this.editForm.get('lastNameSelection');
  }

  public get users(): FormControl
  {
    return this.editForm.get('users');
  }

  public get organizations(): FormControl
  {
    return this.editForm.get('organizations');
  }

  @Input() public set model(integrityEdit: IntegrityEdit)
  {
    this.initModel(integrityEdit);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });
  }

  /* Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public userItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filters the organizations based on user input.
   */
  public filterOrganizations(filter: string): void
  {
    this.searchTerm = filter;
    this.organizationsFiltered = this.allOrganizations
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filters the users based on user input.
   */
  public filterUsers(filter: string): void
  {
    this.searchTerm = filter;
    this.usersFiltered = this.allUsers
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // Users selection for address validations
    if (this.integrityType.value === 'ADDRESS_MEMBER' || this.integrityType.value === 'ADDRESS_CARE_TEAM') 
    {
      if (this.userSelection.value !== 'ALL') 
      {
        if ((!this.users.value || this.users.value.length === 0) &&
          (!this.organizations.value || this.organizations.value.length === 0) &&
          (!this.fromDateSelection.value && !this.toDateSelection.value))
        {
          this.messageHandlerService.displayError('REQUIRED-USERS-SELECTION');
          this.tabstrip.selectTab(1);
          return;
        }
      } 
    }

    // Emit save
    this.editFormActive = false;
    this.save.emit(objectToPersist);
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  private loadLookups(): void
  {
    // Get organizations
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.allOrganizations = data;
        this.filterOrganizations('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.userService.getCachedUsers().subscribe({
      next: (data) =>
      {
        this.allUsers = data;
        this.filterUsers('');
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private initModel(integrityEdit: IntegrityEdit)
  {
    if (integrityEdit === undefined)
    {
      return;
    }

    this.isLocked = this.isNew ? false : integrityEdit.locked;

    this.editForm = this.fb.group({
      id: new FormControl(integrityEdit.id),
      version: new FormControl(integrityEdit.version),
      integrityType: new FormControl({ value: integrityEdit.integrityType, disabled: this.isLocked }, { validators: [Validators.required] }),
      comments: new FormControl({ value: integrityEdit.comments, disabled: this.isLocked }),
      userSelection: new FormControl({ value: integrityEdit.userSelection, disabled: this.isLocked }, { validators: [Validators.required] }),
      automaticValidations: [{ value: this.isNew ? false : integrityEdit.automaticValidations, disabled: this.isLocked }],
      automaticDiscardOnError: [{
        value: this.isNew ? false : integrityEdit.automaticDiscardOnError, disabled: this.isLocked
      }],
      users: [
        { value: integrityEdit.users, disabled: this.isLocked }
      ],
      organizations: [
        { value: integrityEdit.organizations, disabled: this.isLocked }
      ],
      fromDateSelection: new FormControl({ value: integrityEdit.fromDateSelection ? new Date(integrityEdit.fromDateSelection) : null, disabled: this.isLocked }),
      toDateSelection: new FormControl({ value: integrityEdit.toDateSelection ? new Date(integrityEdit.toDateSelection) : null, disabled: this.isLocked }),
      lastNameSelection: new FormControl({ value: integrityEdit.lastNameSelection, disabled: this.isLocked }),
    });

    this.editFormActive = true;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }
}
